import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../ui/button';
import { SPACING, CONTROLS } from '../../lib/design-tokens';

/**
 * FilterChip — Actief filter met verwijder-knop
 */
export const FilterChip = ({ label, value, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">
    <span className="text-blue-400">{label}:</span> {value}
    <button onClick={onRemove} className="ml-0.5 hover:text-blue-900"><X className="h-3 w-3" strokeWidth={2} /></button>
  </span>
);

/**
 * PageFilterBar — Zoek + filter toggle + actieve chips
 * STANDAARD: controls 40px, search min-w-[320px], text 14px
 */
export const PageFilterBar = ({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Zoeken...',
  showFilters,
  onToggleFilters,
  activeFilterCount = 0,
  onClearAll,
  chips = [],
  chipSlot,
  children,
  testId = 'filter',
}) => (
  <div className={`${SPACING.pagePx} ${SPACING.pagePy} bg-white border-b border-neutral-100`} data-testid={`${testId}-bar`}>
    {/* Severity/category chips */}
    {chipSlot && <div className="flex items-center gap-2 mb-3">{chipSlot}</div>}

    {/* Search + filter toggle */}
    <div className="flex items-center gap-2">
      <div className="relative flex-1 min-w-[320px] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" strokeWidth={1.5} />
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className={`w-full pl-10 pr-3 ${CONTROLS.input} border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 bg-white`}
          data-testid={`${testId}-search`}
        />
      </div>
      {onToggleFilters && (
        <Button
          variant="outline"
          className={`${CONTROLS.primary} gap-2 border-neutral-200 ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'text-neutral-600'}`}
          onClick={onToggleFilters}
          data-testid={`${testId}-toggle`}
        >
          <SlidersHorizontal className="h-4 w-4" strokeWidth={1.5} />
          Filters
          {activeFilterCount > 0 && (
            <span className="h-5 w-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </Button>
      )}
      {(activeFilterCount > 0 || searchValue) && onClearAll && (
        <button onClick={onClearAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid={`${testId}-clear`}>
          Wis alles
        </button>
      )}
    </div>

    {/* Extended filter dropdowns */}
    {showFilters && children && (
      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-neutral-100" data-testid={`${testId}-extended`}>
        {children}
      </div>
    )}

    {/* Active filter chips */}
    {chips.length > 0 && (
      <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid={`${testId}-chips`}>
        {chips.map((chip, i) => (
          <FilterChip key={i} label={chip.label} value={chip.value} onRemove={chip.onRemove} />
        ))}
      </div>
    )}
  </div>
);
